import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useCurrentOrganization } from '@/components/features/membership/membership-provider';
import {
  ScheduleForm,
  SchedulingMode,
} from '@/components/features/schedule/forms/schedule-form';
import { Card, CardContent } from '@/components/ui/card';
import { queryClient } from '@/main';
import { updateScheduleMutation } from '@/mutations/updateScheduleMutation';
import { getScheduleQuery } from '@/queries/getScheduleQuery';
import { getQueryKeysFactory } from '@/queries/queryKeysFactory';

export const Route = createFileRoute(
  '/_authenticated/schedule/$scheduleId/edit'
)({
  component: RouteComponent,
});

function RouteComponent() {
  const organization = useCurrentOrganization();
  const { scheduleId } = Route.useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { organizationKeys } = getQueryKeysFactory();
  const { data: schedule } = useQuery({
    ...getScheduleQuery(organization.organizationId, scheduleId),
  });

  const { mutate: updateSchedule, isPending } = useMutation({
    ...updateScheduleMutation({
      organizationId: organization.organizationId,
      scheduleId,
    }),
    onSuccess: (response) => {
      if (response.isErr()) {
        toast.error(
          t('authenticated.schedule.details.edit.toast.genericError')
        );
      } else {
        toast.success(t('authenticated.schedule.details.edit.toast.success'));
        queryClient.invalidateQueries({
          queryKey: organizationKeys.scheduleById(
            organization.organizationId,
            scheduleId
          ),
        });
        navigate({ to: '/schedule/$scheduleId', params: { scheduleId } });
      }
    },
  });

  if (!schedule) {
    return null;
  }

  return (
    <Card>
      <CardContent className="pt-6">
        <ScheduleForm
          onSubmit={updateSchedule}
          submitLabel={t('authenticated.schedule.details.edit.submit')}
          isSubmitting={isPending}
          defaultValues={{
            name: schedule.name,
            description: schedule.description ?? undefined,
            startDate: new Date(schedule.startDate),
            endDate: schedule.endDate ? new Date(schedule.endDate) : null,
            daysOfWeek: schedule.daysOfWeek,
            mode: schedule.mode as SchedulingMode,
            timezone: schedule.timezone,
            color: schedule.color,
            minOnDuty: schedule.minOnDuty ?? 1,
          }}
        />
      </CardContent>
    </Card>
  );
}
